import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, throwError, EMPTY } from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
import {
  ApiService,
  DocumentPresignedType,
  DocumentPresignedUrlData,
} from './api.service';
import { NotificationService } from './notification.service';
import { LoaderService } from './loader.service';

export interface DocumentValidationResult {
  valid: boolean;
  error?: string;
}

export interface ExtractedPdf {
  url?: string;
  base64?: string;
  fileName: string;
}

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  private activeRequests = new Set<string>();
  private loadingSubject = new BehaviorSubject<boolean>(false);
  public loading$ = this.loadingSubject.asObservable();

  constructor(
    private apiService: ApiService,
    private notificationService: NotificationService,
    private loaderService: LoaderService,
  ) {}

  /**
   * Check if any document request or a specific document request is in flight.
   */
  isLoading(requestKey?: string): boolean {
    if (requestKey) {
      return this.activeRequests.has(requestKey);
    }
    return this.activeRequests.size > 0;
  }

  /**
   * Generate a unique request key for debouncing duplicate requests.
   */
  getRequestKey(
    documentType: DocumentPresignedType,
    data: DocumentPresignedUrlData,
  ): string {
    const inv = data?.invoice_number?.trim() || '';
    const book = data?.booking_number?.trim() || '';
    const cred = data?.credit_note_number?.trim() || '';
    return `${documentType}:${inv}:${book}:${cred}`;
  }

  /**
   * Validate mandatory fields for a specific document type before invoking API.
   * - tax_invoice: invoice_number is mandatory
   * - booking: booking_number is mandatory
   * - credit_note: both invoice_number and credit_note_number are mandatory
   * - proforma: booking_number is mandatory
   */
  validate(
    documentType: DocumentPresignedType,
    data: DocumentPresignedUrlData,
  ): DocumentValidationResult {
    if (!data) {
      return {
        valid: false,
        error: 'Document details are required.',
      };
    }

    switch (documentType) {
      case 'tax_invoice': {
        const invoiceNumber = data.invoice_number?.trim();
        if (!invoiceNumber) {
          return {
            valid: false,
            error: 'Invoice number is required to download or view Tax Invoice.',
          };
        }
        return { valid: true };
      }

      case 'booking': {
        const bookingNumber = data.booking_number?.trim();
        if (!bookingNumber) {
          return {
            valid: false,
            error:
              'Booking number is required to download or view Booking document.',
          };
        }
        return { valid: true };
      }

      case 'credit_note': {
        const invoiceNumber = data.invoice_number?.trim();
        const creditNoteNumber = data.credit_note_number?.trim();
        if (!invoiceNumber || !creditNoteNumber) {
          return {
            valid: false,
            error:
              'Both Invoice number and Credit Note number are required to download or view Credit Note.',
          };
        }
        return { valid: true };
      }

      case 'proforma': {
        const bookingNumber = data.booking_number?.trim();
        if (!bookingNumber) {
          return {
            valid: false,
            error:
              'Booking number is required to download or view Proforma invoice.',
          };
        }
        return { valid: true };
      }

      default:
        return {
          valid: false,
          error: 'Unsupported document type specified.',
        };
    }
  }

  /**
   * Extract presigned document URL from various common response structures.
   */
  extractPresignedUrl(response: any): string | null {
    if (!response) {
      return null;
    }

    if (typeof response === 'string' && response.startsWith('http')) {
      return response.trim();
    }

    const candidate =
      response?.url ||
      response?.data?.url ||
      response?.data?.presigned_url ||
      response?.data?.document_url ||
      response?.data?.pdf_url ||
      response?.RESPONSE?.DATA?.url ||
      response?.RESPONSE?.DATA?.presigned_url ||
      (typeof response?.data === 'string' && response.data.startsWith('http')
        ? response.data
        : null);

    return typeof candidate === 'string' && candidate.trim().length > 0
      ? candidate.trim()
      : null;
  }

  /**
   * User-friendly error message resolution without exposing sensitive internals.
   */
  getErrorMessage(error: any): string {
    if (!error) {
      return 'Failed to generate document. Please try again.';
    }

    if (error.status === 0) {
      return 'Network connection error. Please verify your internet connection.';
    }

    if (error.status === 400) {
      return (
        error.error?.message ||
        error.error?.msg ||
        'Invalid document request. Please check document details.'
      );
    }

    if (error.status === 401) {
      return 'Session expired or unauthorized. Please log in again.';
    }

    if (error.status === 403) {
      return 'You do not have permission to view or download this document.';
    }

    if (error.status === 404) {
      return 'Requested document not found.';
    }

    if (error.status >= 500) {
      return 'Server encountered an issue while generating document. Please try again later.';
    }

    return (
      error.error?.message ||
      error.error?.msg ||
      error.message ||
      'Failed to generate document. Please try again.'
    );
  }

  /**
   * Single reusable method for requesting presigned URL from ApiService.
   */
  getDocumentPresignedUrl(
    documentType: DocumentPresignedType,
    data: DocumentPresignedUrlData,
  ): Observable<any> {
    return this.apiService.getDocumentPresignedUrl(documentType, data);
  }

  /**
   * Validates parameters, prevents duplicate clicks, sets loading state, fetches the
   * presigned URL, and opens the document in a new browser tab.
   *
   * Note: The presigned URL expires after 900 seconds and is never persisted
   * in localStorage or sessionStorage.
   */
  openDocument(
    documentType: DocumentPresignedType,
    data: DocumentPresignedUrlData,
  ): Observable<string | null> {
    const requestKey = this.getRequestKey(documentType, data);

    // Prevent duplicate concurrent requests (e.g. double-click)
    if (this.activeRequests.has(requestKey)) {
      return EMPTY;
    }

    // Step 1: Validate mandatory fields
    const validation = this.validate(documentType, data);
    if (!validation.valid) {
      this.notificationService.error(validation.error || 'Invalid parameters.');
      return throwError(() => new Error(validation.error));
    }

    // Step 2: Set loading state
    this.activeRequests.add(requestKey);
    this.loadingSubject.next(true);
    this.loaderService.show();

    // Step 3: Call API and process response
    return this.getDocumentPresignedUrl(documentType, data).pipe(
      map((response) => {
        // Check for business failure flag if present in body
        if (response && response.success === false) {
          const failureMsg =
            response.message ||
            response.data?.msg ||
            response.data?.message ||
            'Document generation failed.';
          throw new Error(failureMsg);
        }

        const presignedUrl = this.extractPresignedUrl(response);
        if (!presignedUrl) {
          throw new Error('Presigned document URL not received from server.');
        }

        // Step 4: Open document in new tab without storing in localStorage/sessionStorage
        window.open(presignedUrl, '_blank');
        return presignedUrl;
      }),
      catchError((error) => {
        const errorMsg = this.getErrorMessage(error);
        this.notificationService.error(errorMsg);
        // Do not log sensitive authentication headers/tokens
        console.error('Document fetch failed with status:', error?.status || 'Error');
        return throwError(() => error);
      }),
      finalize(() => {
        this.activeRequests.delete(requestKey);
        this.loadingSubject.next(this.activeRequests.size > 0);
        this.loaderService.hide();
      }),
    );
  }

  /**
   * Extract PDF document data (URL or base64) and fileName from API response.
   */
  extractPdfData(
    response: any,
    fallbackName: string = 'invoice.pdf',
  ): ExtractedPdf | null {
    if (!response) {
      return null;
    }

    // Candidate URL extraction
    const candidateUrl =
      response?.url ||
      response?.data?.url ||
      response?.data?.presigned_url ||
      response?.data?.document_url ||
      response?.data?.pdf_url ||
      response?.data?.download_url ||
      response?.data?.download_pdf ||
      response?.download_url ||
      response?.download_pdf ||
      response?.RESPONSE?.DATA?.url ||
      response?.RESPONSE?.DATA?.presigned_url ||
      (typeof response?.data === 'string' &&
      (response.data.startsWith('http://') ||
        response.data.startsWith('https://') ||
        response.data.startsWith('blob:'))
        ? response.data
        : null) ||
      (typeof response === 'string' &&
      (response.startsWith('http://') ||
        response.startsWith('https://') ||
        response.startsWith('blob:'))
        ? response
        : null);

    // Resolve fileName
    let fileName =
      response?.data?.file_name ||
      response?.data?.filename ||
      response?.file_name ||
      response?.filename ||
      response?.data?.name ||
      response?.data?.invoice_number ||
      response?.invoice_number;

    if (!fileName && candidateUrl) {
      try {
        const decodedUrl = decodeURIComponent(candidateUrl);
        const match = decodedUrl.match(
          /filename\*?=['"]?(?:UTF-\d['"]*)?([^;\r\n"']+)['"]?/i,
        );
        if (match && match[1]) {
          fileName = match[1].trim();
        } else {
          const urlObj = new URL(candidateUrl);
          const lastSegment = urlObj.pathname.substring(
            urlObj.pathname.lastIndexOf('/') + 1,
          );
          if (lastSegment && lastSegment.toLowerCase().endsWith('.pdf')) {
            fileName = lastSegment;
          }
        }
      } catch {}
    }

    if (!fileName) {
      fileName = fallbackName;
    }

    if (!fileName.toLowerCase().endsWith('.pdf')) {
      fileName += '.pdf';
    }

    if (
      candidateUrl &&
      typeof candidateUrl === 'string' &&
      candidateUrl.trim().length > 0
    ) {
      return { url: candidateUrl.trim(), fileName };
    }

    // Candidate Base64 extraction
    const candidateBase64 =
      response?.data?.base64 ||
      response?.base64 ||
      response?.data?.pdf_base64 ||
      response?.data?.pdf ||
      response?.pdf ||
      response?.data?.file_data ||
      response?.file_data ||
      (typeof response?.data === 'string' &&
      (response.data.startsWith('data:application/pdf') ||
        response.data.startsWith('JVBERi'))
        ? response.data
        : null) ||
      (typeof response === 'string' &&
      (response.startsWith('data:application/pdf') ||
        response.startsWith('JVBERi'))
        ? response
        : null);

    if (
      candidateBase64 &&
      typeof candidateBase64 === 'string' &&
      candidateBase64.trim().length > 0
    ) {
      return { base64: candidateBase64.trim(), fileName };
    }

    return null;
  }

  /**
   * Automatically downloads a PDF to user's device.
   * Handles URLs via fetch-to-blob with anchor fallback for CORS, or direct base64-to-blob.
   */
  async downloadPdfFile(pdf: ExtractedPdf): Promise<void> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      return;
    }

    if (pdf.base64) {
      this.downloadFromBase64(pdf.base64, pdf.fileName);
      return;
    }

    if (pdf.url) {
      await this.downloadFromUrl(pdf.url, pdf.fileName);
    }
  }

  private downloadFromBase64(base64Data: string, fileName: string): void {
    let cleanBase64 = base64Data.trim();
    if (cleanBase64.startsWith('data:')) {
      const commaIdx = cleanBase64.indexOf(',');
      if (commaIdx !== -1) {
        cleanBase64 = cleanBase64.substring(commaIdx + 1);
      }
    }
    const byteCharacters = atob(cleanBase64);
    const byteNumbers = new Uint8Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([byteNumbers], { type: 'application/pdf' });
    this.triggerBlobDownload(blob, fileName);
  }

  private downloadFromUrl(url: string, fileName: string): void {
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName || 'invoice.pdf');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private triggerBlobDownload(blob: Blob, fileName: string): void {
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => {
      window.URL.revokeObjectURL(blobUrl);
    }, 1500);
  }

  /**
   * Calls ApiService.smsinvoiceDownload with the short code.
   */
  downloadSmsInvoice(shortCode: string): Observable<any> {
    return this.apiService.smsinvoiceDownload({ short_code: shortCode });
  }
}
